﻿import * as Core from './arcGisJsInterop';
import Editor from "@arcgis/core/widgets/Editor";
import Sketch from "@arcgis/core/widgets/Sketch";
import Track from "@arcgis/core/widgets/Track";
import MapView from "@arcgis/core/views/MapView";
import Widget from "@arcgis/core/widgets/Widget";
import SnappingOptions from "@arcgis/core/views/interactive/snapping/SnappingOptions";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { buildDotNetGraphic, buildDotNetLayer } from './dotNetBuilder';
import {
    buildJsFormTemplate,
    buildJsGraphic,
    buildJsSymbol,
    buildJsTimeSliderStops,
    buildJsTickConfigs,
    buildJsFeatureReduction
} from "./jsBuilder";
import LocatorWrapper from "./locator";
import ArcGisRestWrapper from "./arcGisRest";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Swipe from "@arcgis/core/widgets/Swipe";
import EditorWidgetWrapper from "./editorWidgetWrapper";
import FeatureForm from "@arcgis/core/widgets/FeatureForm";
import FeatureFormWidgetWrapper from "./featureFormWidgetWrapper";
import Layer from '@arcgis/core/layers/Layer';
import FeatureTemplates from "@arcgis/core/widgets/FeatureTemplates";
import SketchTooltipOptions from "@arcgis/core/views/interactive/sketch/SketchTooltipOptions";
import FeatureFormViewModel from "@arcgis/core/widgets/FeatureForm/FeatureFormViewModel";
import FeatureTemplatesViewModel from "@arcgis/core/widgets/FeatureTemplates/FeatureTemplatesViewModel";
import FeatureTemplatesWidgetWrapper from "./featureTemplatesWidgetWrapper";
import SketchWidgetWrapper from "./sketchWidgetWrapper";
import TrackWidgetWrapper from "./trackWidgetWrapper";
import SketchLabelOptions from "@arcgis/core/views/interactive/sketch/SketchLabelOptions";
import TimeSlider from "@arcgis/core/widgets/TimeSlider";
import TimeSliderWidgetWrapper from "./timeSliderWidgetWrapper";
import Graphic from "@arcgis/core/Graphic";
import {arcGisObjectRefs, updateLayer} from "./arcGisJsInterop";
import FeatureSnappingLayerSource from "@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource";
import PopupTemplateCreatorWrapper from "./popupTemplateCreator";
import LabelCreatorWrapper from "./labelCreator";
import PieChartCreatorWrapper from "./pieChartCreator";
import View from '@arcgis/core/views/View';
import NetworkServiceWrapper from "./networkService";
import RouteWrapper from "./route";
import {dot} from "node:test/reporters";

export function getCore(): any {
    return Core;
}

export async function getArcGisRestWrapper(dotNetRef: any): Promise<ArcGisRestWrapper> {
    if (Core.ProtoGraphicCollection === undefined) {
        await Core.loadProtobuf();
    }
    return new ArcGisRestWrapper(dotNetRef);
}

export function getPopupTemplateCreatorWrapper(dotNetRef: any): any {
    return new PopupTemplateCreatorWrapper(dotNetRef);
}

export function getLabelCreatorWrapper(dotNetRef: any): any {
    return new LabelCreatorWrapper(dotNetRef);
}

export function getPieChartCreatorWrapper(dotNetRef: any): any {
    return new PieChartCreatorWrapper(dotNetRef);
}

export async function getNetworkServiceWrapper(dotNetRef: any): Promise<NetworkServiceWrapper> {
    if (Core.ProtoGraphicCollection === undefined) {
        await Core.loadProtobuf();
    }
    return new NetworkServiceWrapper(dotNetRef);
}

export async function getRouteWrapper(dotNetRef: any): Promise<RouteWrapper> {
    if (Core.ProtoGraphicCollection === undefined) {
        await Core.loadProtobuf();
    }
    return new RouteWrapper(dotNetRef);
}

export function setFeatureReduction(featureReduction: any, layerId: string, viewId: string) {
    let layer = arcGisObjectRefs[layerId] as FeatureLayer;
    if (layer === undefined || layer === null) return;
    layer.featureReduction = buildJsFeatureReduction(featureReduction, viewId);
} 

export async function refreshProWidget(widget, id, viewId) {
    try {
        setWaitCursor(viewId);

        await addProWidget(widget, viewId);
        unsetWaitCursor(viewId);
    } catch (error) {
        logError(error, viewId);
    }
}

export function addGraphicsProSyncInterop(graphicsArray: Uint8Array, viewId: string, layerId?: string | null): void {
    try {
        let graphics = Core.decodeProtobufGraphics(graphicsArray);
        let jsGraphics: Graphic[] = [];
        let view = arcGisObjectRefs[viewId] as View;
        for (const g of graphics) {
            let jsGraphic = buildJsGraphic(g, viewId) as Graphic;
            jsGraphics.push(jsGraphic);
        }
        if (hasValue(layerId)) {
            let layer = arcGisObjectRefs[layerId as string] as GraphicsLayer;
            layer.graphics?.addMany(jsGraphics);
        } else {
            view.graphics?.addMany(jsGraphics);
        }
        (async () => {
            for (let i = 0; i < jsGraphics.length; i++) {
                let graphic = jsGraphics[i];
                let graphicObject = graphics[i];
                Core.graphicsRefs[graphicObject.id] = graphic;
            }
        })();
    } catch (error) {
        logError(error, viewId);
    }
}

export async function addProWidget(widget: any, viewId: string): Promise<void> {
    try {
        let view = Core.arcGisObjectRefs[viewId] as MapView;
        if (view === undefined || view === null) return;
        let newWidget: Widget;
        let wrapper: any;
        switch (widget.type) {
            case 'sketch':
                newWidget = await createSketchWidget(widget, view, viewId);
                wrapper = new SketchWidgetWrapper(newWidget as Sketch);
                break;
            case 'editor':
                const editor = new Editor({
                    view: view,
                    container: widget.containerId ?? undefined
                });

                if (hasValue(widget.layerInfos)) {
                    editor.layerInfos = [];
                    for (let i = 0; i < widget.layerInfos.length; i++) {
                        let layerInfo: any = {
                            layer: Core.arcGisObjectRefs[widget.layerInfos[i].id] as FeatureLayer
                        };
                        if (hasValue(widget.layerInfos[i].formTemplate)) {
                            layerInfo.formTemplate = buildJsFormTemplate(widget.layerInfos[i].formTemplate);
                        }
                        editor.layerInfos.push(layerInfo);
                    }
                }
                if (hasValue(widget.snappingOptions)) {
                    
                    if (hasValue(widget.snappingOptions.featureSources) && widget.snappingOptions.featureSources.length > 0) {
                        editor.snappingOptions = new SnappingOptions({
                            featureSources: buildFeatureSnappingLayerSources(widget.snappingOptions.featureSources)
                        })
                    } else {
                        editor.snappingOptions = new SnappingOptions();
                    }
                    if (hasValue(widget.snappingOptions.enabled)) {
                        editor.snappingOptions.enabled = widget.snappingOptions.enabled;
                    }
                }
                if (hasValue(widget.labelOptions)) {
                    editor.labelOptions = new SketchLabelOptions();
                    if (hasValue(widget.labelOptions.enabled)) {
                        editor.labelOptions.enabled = widget.labelOptions.enabled;
                    }
                }
                if (hasValue(widget.tooltipOptions)) {
                    let options: any = {};
                    if (hasValue(widget.tooltipOptions.enabled)) {
                        options.enabled = widget.tooltipOptions.enabled;
                    }
                    editor.tooltipOptions = options as SketchTooltipOptions;
                }
                if (hasValue(widget.supportingWidgetDefaults)) {
                    editor.supportingWidgetDefaults = buildSupportingWidgetDefaults(widget.supportingWidgetDefaults);
                }
                if (hasValue(widget.visibleElements)) {
                    editor.visibleElements = buildEditorVisibleElements(widget.visibleElements);
                }

                copyValuesIfExists(widget, editor, 'allowedWorkflows', 'visible',
                    'headingLevel', 'label');

                newWidget = editor;
                wrapper = new EditorWidgetWrapper(editor);
                break;
            case 'track':
                const track = new Track({
                    view: view,
                    container: widget.containerId ?? undefined
                });
                if (hasValue(widget.graphic)) {
                    track.graphic = buildJsGraphic(widget.graphic, viewId);
                }

                copyValuesIfExists(track, widget, 'rotationEnabled', 'geoLocationOptions', 'goToLocationEnabled',
                    'label', 'scale', 'visible')
                newWidget = track;
                wrapper = new TrackWidgetWrapper(track);
                let eventRateLimit = widget.eventRateLimitInMilliseconds;
                let lastEventCall: number = 0;
                track.on('track', (trackEvent) => {
                    let now = Date.now();
                    if (eventRateLimit !== undefined && eventRateLimit !== null &&
                        lastEventCall + eventRateLimit > now) {
                        return;
                    }
                    lastEventCall = now;

                    widget.dotNetWidgetReference.invokeMethodAsync('OnJsTrack', trackEvent);
                });
                break;
            case 'swipe':
                let leadingLayers = widget.leadingLayers.map(l => Core.arcGisObjectRefs[l.id]);
                let trailingLayers = widget.trailingLayers.map(l => Core.arcGisObjectRefs[l.id]);
                const swipe = new Swipe({
                    view: view,
                    leadingLayers: leadingLayers,
                    trailingLayers: trailingLayers
                });
                if (hasValue(widget.swipePosition)) {
                    swipe.position = widget.swipePosition;
                }
                copyValuesIfExists(widget, swipe, 'direction', 'disabled', 'dragLabel', 'iconClass', 'label');
                newWidget = swipe;
                wrapper = newWidget;
                break;
            case 'feature-form':
                let layer = Core.arcGisObjectRefs[widget.layerId] as FeatureLayer;
                const featureForm = new FeatureForm({
                    map: view.map,
                    groupDisplay: widget.groupDisplay,
                    disabled: widget.disabled ?? undefined,
                    container: widget.containerId ?? undefined,
                    layer: layer
                });
                if (hasValue(widget.formTemplate)) {
                    featureForm.formTemplate = buildJsFormTemplate(widget.formTemplate);
                }

                if (hasValue(widget.viewModel)) {
                    featureForm.viewModel = buildFeatureFormViewModel(widget.viewModel, layer, view.map);
                }

                copyValuesIfExists(widget, featureForm, 'headingLevel', 'label');
                newWidget = featureForm;
                featureForm.on('submit', (event) => {
                    widget.dotNetWidgetReference.invokeMethodAsync("OnJsSubmit", event);
                });
                featureForm.on('value-change', (event) => {
                    let layerGeoBlazorId = Object.keys(Core.arcGisObjectRefs).find(key => Core.arcGisObjectRefs[key] === event.layer);
                    widget.dotNetWidgetReference.invokeMethodAsync("OnJsValueChange", {
                        layerId: layerGeoBlazorId,
                        feature: buildDotNetGraphic(event.feature),
                        fieldName: event.fieldName,
                        value: event.value,
                        valid: event.valid
                    });
                });

                wrapper = new FeatureFormWidgetWrapper(featureForm);
                break;

            case 'feature-templates':
                let layers = [];
                if (hasValue(widget.layerIds)) {
                    layers = widget.layerIds.map(i => Core.arcGisObjectRefs[i] as Layer);
                }
                const featureTemplates = new FeatureTemplates({
                    layers: layers as any,
                    container: widget.containerId ?? undefined
                });

                if (hasValue(widget.viewModel)) {
                    featureTemplates.viewModel = buildFeatureTemplatesViewModel(widget.viewModel);
                }

                copyValuesIfExists(widget, featureTemplates, 'enableListScroll', 'headingLevel', 'label');
                newWidget = featureTemplates;
                featureTemplates.on('select', (event) => {
                    widget.dotNetWidgetReference.invokeMethodAsync("OnJsSelect", event);
                });
                wrapper = new FeatureTemplatesWidgetWrapper(featureTemplates);
                break;
            case 'time-slider':

                let labelFormat = null;

                if (widget.labelFormatFunction != null) {
                    labelFormat = new Function(widget.labelFormatFunction);
                }

                let timeSlider = new TimeSlider({
                    disabled: widget.disabled ?? undefined,
                    container: widget.container ?? undefined,
                    view: view,
                    timeVisible: widget.timeVisible ?? undefined,
                    loop: widget.loop ?? undefined,
                    mode: widget.mode ?? undefined,
                    stops: buildJsTimeSliderStops(widget.stops) ?? undefined,
                    label: widget.label ?? undefined,
                    labelFormatFunction: labelFormat ?? undefined,
                    actions: widget.actions ?? undefined
                });
                timeSlider.fullTimeExtent = widget.fullTimeExtent ?? undefined;

                if (hasValue(widget.tickConfigs) && widget.tickConfigs.length > 0) {
                    timeSlider.tickConfigs = widget.tickConfigs.map(buildJsTickConfigs);
                }

                newWidget = timeSlider;
                wrapper = new TimeSliderWidgetWrapper(timeSlider);
                break;
        }
        if (hasValue(widget.widgetId)) {
            newWidget.id = widget.widgetId;
        }
        copyValuesIfExists(widget, newWidget, 'icon');

        //handling widget refresh
        Core.arcGisObjectRefs[widget.id] = newWidget;
        if (hasValue(widget.containerId)) {
            let container = document.getElementById(widget.containerId);
            let innerContainer = document.createElement('div');
            innerContainer.id = `widget-${widget.type}`;
            let existingWidget = document.getElementById(`widget-${widget.type}`);
            if (existingWidget !== null) {
                container?.removeChild(existingWidget);
            }
            container?.appendChild(innerContainer);
            newWidget.container = innerContainer;
        } else {
            view.ui.add(newWidget, widget.position);
        }
        if (hasValue(widget.dotNetWidgetReference)) {
            // @ts-ignore
            await widget.dotNetWidgetReference.invokeMethodAsync("OnWidgetJSRegistered", DotNet.createJSObjectReference(wrapper));
        }
    } catch (error) {
        logError(error, viewId);
    }
}

async function createSketchWidget(widget: any, view: MapView, viewId: string): Promise<Sketch> {
    let graphicsLayer = Core.arcGisObjectRefs[widget.graphicsLayerId] as GraphicsLayer;
    let eventRateLimit = widget.eventRateLimitInMilliseconds;
    const sketch = new Sketch({
        view: view,
        layer: graphicsLayer,
        creationMode: widget.creationMode,
        container: widget.containerId ?? undefined
    });

    if (hasValue(widget.snappingOptions)) {
        sketch.snappingOptions = new SnappingOptions({
            enabled: widget.snappingOptions.enabled,
            featureSources: buildFeatureSnappingLayerSources(widget.snappingOptions.featureSources) ?? [{
                enabled: true,
                layer: graphicsLayer
            }]
        });
    }
    if (hasValue(widget.labelOptions)) {
        sketch.labelOptions = new SketchLabelOptions({
            enabled: widget.labelOptions.enabled
        });
    }
    if (hasValue(widget.tooltipOptions)) {
        sketch.tooltipOptions = new SketchTooltipOptions({
            enabled: widget.labelOptions.enabled
        });
    }
    if (hasValue(widget.visibleElements)) {
        sketch.visibleElements = buildSketchVisibleElements(widget.visibleElements);
    }

    copyValuesIfExists(widget, sketch, 'availableCreateTools', 'updateOnGraphicClick',
        'defaultCreateOptions', 'defaultUpdateOptions', 'label', 'layout', 'visible');

    let lastEventCall: number = 0;
    (sketch as any).on(["create", "update", "undo", "redo"], async (event) => {
        try {
            let sketchEvent = {
                state: event.state,
                type: event.type,
                tool: event.tool,
                graphics: [],
                toolEventInfo: buildToolEventInfo(event.toolEventInfo)
            };

            if (event.type === 'create') {
                if (hasValue(event.graphic)) {
                    let sketchGraphic = buildDotNetGraphic(event.graphic);
                    sketchEvent.graphics.push(sketchGraphic);
                    if (event.state == 'complete') {
                        sketchGraphic.id = await Core.dotNetRefs[viewId].invokeMethodAsync('GetId');
                        Core.graphicsRefs[sketchGraphic.id] = event.graphic;
                    }
                }
            } else {
                event.graphics?.forEach(g => {
                    let sketchGraphic = buildDotNetGraphic(g);
                    sketchEvent.graphics.push(sketchGraphic);
                });
                if (event.state == 'active') {
                    let now = Date.now();
                    if (eventRateLimit !== undefined && eventRateLimit !== null &&
                        lastEventCall + eventRateLimit > now) {
                        return;
                    }
                    lastEventCall = now;
                }
            }

            await widget.sketchWidgetObjectReference.invokeMethodAsync('OnSketchUpdate', sketchEvent);
        }
        catch (error) {
            logError(error, (view as any).id);
        }
    });

    return sketch;
}

function buildFeatureSnappingLayerSources(dotNetFsls: []): FeatureSnappingLayerSource[] | null {
    if (!hasValue(dotNetFsls) || dotNetFsls.length == 0) return null;
    return dotNetFsls.map(f => buildFeatureSnappingLayerSource(f));
}

function buildFeatureSnappingLayerSource(dotNetFsls: any): FeatureSnappingLayerSource {
    let source: any = {};
    if (hasValue(dotNetFsls.layerId)) {
        source.layer = Core.arcGisObjectRefs[dotNetFsls.layerId] as Layer;
    }
    if (hasValue(dotNetFsls.enabled)) {
        source.enabled = dotNetFsls.enabled;
    }
    return source as FeatureSnappingLayerSource;
}

function buildSketchVisibleElements(dotNetVisibleElements: any): any {
    return {
        settingsMenu: dotNetVisibleElements.settingsMenu ?? undefined,
        undoRedoMenu: dotNetVisibleElements.undoRedoMenu ?? undefined,
        snappingControls: dotNetVisibleElements.snappingControls ?? undefined,
        duplicateButton: dotNetVisibleElements.duplicateButton ?? undefined,
        createTools: hasValue(dotNetVisibleElements.createTools) ? {
            point: dotNetVisibleElements.createTools.point ?? true,
            polyline: dotNetVisibleElements.createTools.polyline ?? true,
            polygon: dotNetVisibleElements.createTools.polygon ?? true,
            circle: dotNetVisibleElements.createTools.circle ?? true,
            rectangle: dotNetVisibleElements.createTools.rectangle ?? true
        } : undefined,
        selectionTools: hasValue(dotNetVisibleElements.selectionTools) ? {
            "lasso-selection": dotNetVisibleElements.selectionTools.lassoSelection ?? undefined,
            "rectangle-selection": dotNetVisibleElements.selectionTools.rectangleSelection ?? undefined
        } : undefined,
        snappingControlsElements: hasValue(dotNetVisibleElements.snappingControlsElements) ? {
            header: dotNetVisibleElements.snappingControlsElements.header ?? undefined,
            enabledToggle: dotNetVisibleElements.snappingControlsElements.enabledToggle ?? undefined,
            selfEnabledToggle: dotNetVisibleElements.snappingControlsElements.selfEnabledToggle ?? undefined,
            featureEnabledToggle: dotNetVisibleElements.snappingControlsElements.featureEnabledToggle ?? undefined,
            layerList: dotNetVisibleElements.snappingControlsElements.layerList ?? undefined
        } : undefined
    };
}

function buildSupportingWidgetDefaults(dotNetDefaults: any): any {
    return {
        attachments: dotNetDefaults.attachments ?? undefined,
        featureForm: dotNetDefaults.featureForm ?? undefined,
        featureTemplates: dotNetDefaults.featureTemplates ?? undefined,
        sketch: hasValue(dotNetDefaults.sketch) ? {
            defaultUpdateOptions: dotNetDefaults.sketch.defaultUpdateOptions ?? undefined,
            markerSymbol: buildJsSymbol(dotNetDefaults.sketch.markerSymbol) ?? undefined,
            polylineSymbol: buildJsSymbol(dotNetDefaults.sketch.polylineSymbol) ?? undefined,
            polygonSymbol: buildJsSymbol(dotNetDefaults.sketch.polygonSymbol) ?? undefined
        } : undefined
    };
}

function buildEditorVisibleElements(visibleElements: any): any {
    return {
        sketchTooltipControls: visibleElements.sketchTooltipControls ?? undefined,
        snappingControls: visibleElements.snappingControls ?? undefined,
        snappingControlsElements: hasValue(visibleElements.snappingControlsElements) ? {
            header: visibleElements.snappingControlsElements.header ?? undefined,
            enabledToggle: visibleElements.snappingControlsElements.enabledToggle ?? undefined,
            selfEnabledToggle: visibleElements.snappingControlsElements.selfEnabledToggle ?? undefined,
            featureEnabledToggle: visibleElements.snappingControlsElements.featureEnabledToggle ?? undefined,
            layerList: visibleElements.snappingControlsElements.layerList ?? undefined,
        } : undefined
    };
}

function buildFeatureFormViewModel(dotNetVm: any, layer: Layer, map: any): FeatureFormViewModel {
    return {
        feature: buildJsGraphic(dotNetVm.feature, null) ?? undefined,
        formTemplate: buildJsFormTemplate(dotNetVm.formTemplate) ?? undefined,
        layer: layer,
        map: map,
        submittable: dotNetVm.submittable ?? undefined
    } as FeatureFormViewModel;
}

function buildFeatureTemplatesViewModel(dotNetVm: any): FeatureTemplatesViewModel {
    return {

    } as FeatureTemplatesViewModel;
}

function buildToolEventInfo(eventInfo: any): any {
    if (!hasValue(eventInfo)) return undefined;
    switch (eventInfo.type) {
        case 'move':
        case 'move-start':
        case 'move-stop':
            return {
                type: eventInfo.type,
                dx: eventInfo.dx,
                dy: eventInfo.dy,
                mover: buildDotNetGraphic(eventInfo.mover)
            };
        case 'selection-change':
            return {
                type: eventInfo.type,
                added: eventInfo.added.map(g => buildDotNetGraphic(g)),
                removed: eventInfo.removed.map(g => buildDotNetGraphic(g))
            };
    }

    return eventInfo;
}

function logError(error, viewId) {
    if (error.stack !== undefined && error.stack !== null) {
        console.log(error.stack);
        Core.dotNetRefs[viewId]?.invokeMethodAsync('OnJavascriptError', error.stack);
    } else {
        console.log(error.message);
        Core.dotNetRefs[viewId]?.invokeMethodAsync('OnJavascriptError', error.message);
    }
    unsetWaitCursor(viewId);
}

function setWaitCursor(viewId) {
    let viewContainer = document.getElementById(`map-container-${viewId}`);
    if (viewContainer !== null) {
        viewContainer.style.cursor = 'wait';
    }
}

function unsetWaitCursor(viewId) {
    let viewContainer = document.getElementById(`map-container-${viewId}`);
    if (viewContainer !== null) {
        viewContainer.style.cursor = 'unset';
    }
}

function hasValue(prop: any): boolean {
    return prop !== undefined && prop !== null;
}

function copyValuesIfExists(originalObject: any, newObject: any, ...params: Array<string>) {
    params.forEach(p => {
        if (hasValue(originalObject[p]) && originalObject[p] !== newObject[p]) {
            newObject[p] = originalObject[p];
        }
    });
}

// encryption
export async function decryptKey(licenseObject: string, signature: string, publicKey: string): Promise<boolean> {
    const key = await crypto.subtle.importKey("spki", base64StringtoArrayBuffer(publicKey), {
        name: "RSASSA-PKCS1-v1_5",
        hash: {name: "SHA-256"}
    }, false, ["verify"]);
    const valid = await crypto.subtle.verify({name: "RSASSA-PKCS1-v1_5"}, key, 
        base64StringtoArrayBuffer(signature), base64StringtoArrayBuffer(licenseObject));
    return valid;
}

function base64StringtoArrayBuffer(b64str) {
    const str = window.atob(b64str);
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }
    return buf;
}